import { useCallback, useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import type { Query, QueryClient, QueryKey } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { captureEvent, AA_EVENTS } from "src/config/posthog";
import { CHUNKED_UPLOAD_THRESHOLD } from "src/constants";
import { retryUnlessClientError } from "src/config/react-query";
import { FAILURE_STATUSES } from "src/helpers/processingStatus";
import {
  useNotificationStreams,
  useSSEFallbackInvalidation,
} from "src/reusable/hooks/useNotificationStream";
import { uploadFileInChunks } from "src/reusable/services/unifiedChunkedUploadService";
import type { RevisionChainResponse } from "src/api/records/types";
import type { SDRStatusEvent } from "src/types/notificationEvents";
import type {
  ProcessingStatusData,
  SourceDataRecord,
} from "src/views/datasets/types";

// A single content chunk of an indexed document, as served by the folder
// chunks endpoint. Shape mirrors orm.DataChunk fields the viewer reads.
export interface DocumentChunk {
  id: number;
  chunk_data: string;
  chunk_metadata: { page_number?: number | number[] } & Record<string, any>;
  create_date: string;
}
import type {
  AccessGapRequest,
  AccessGapResponse,
  ProjectFolder,
  ProjectFolderAncestor,
  FolderSharesResponse,
  ProjectFile,
  CreateProjectRequest,
  CreateFolderRequest,
  UpdateFolderRequest,
  AddFromWorkspaceRequest,
  FileUploadResponse,
  FolderSearchResponse,
  MoveFileRequest,
  TransferOwnershipRequest,
  UserShareInfo,
  GroupShareInfo,
  ContextSnapshotResponse,
  ProjectFileProcessingStatus,
  FolderProcessingSummary,
  FolderProcessingStatusResponse,
} from "./types";

// Payload cap for the still-unpaginated file listings (folder files,
// archived files). Folder *folder* listings now page properly (see
// useFolders); file pagination is a separate follow-up, so these keep the
// forward-compatible 500-row cap.
const LIST_HARD_CAP = 500;

// Page size for the folder listing. The backend bounds `limit` at 500; we
// page smaller so a folder with many children doesn't ship one giant payload,
// and surface a "load more" row when `total` exceeds what's loaded. Typical
// folders fit in one page, so the affordance only appears when it's needed.
const FOLDER_PAGE_SIZE = 100;

/**
 * Invalidate caches after a write, without adopting a read that predates it.
 *
 * `invalidateQueries` alone is not enough. react-query v3 dedupes: when a
 * fetch is already running, `Query.fetch` returns that promise and issues no
 * request unless `cancelRefetch` is set (`core/query.js`), and
 * `invalidateQueries` does not set it — that became the default only in v4. So
 * an invalidation landing while a background read is in flight is silently
 * swallowed, and the response that resolves into the cache was issued *before*
 * the write. With `staleTime` set and no focus refetch, nothing corrects it
 * until a reload (#2454: a created project missing from the pane).
 *
 * Note the argument position: given a key first, `invalidateQueries` merges its
 * second argument into the *filters* and reads options from the third
 * (`parseFilterArgs`), so `{ cancelRefetch: true }` passed second is silently
 * ignored rather than rejected.
 *
 * `cancelRefetch` only cancels once the query holds data — an in-flight *first*
 * load is still adopted. That is the right trade here: cancelling it would
 * revert to no-data and flash the pane's loading state, and a write cannot
 * race a first load the user has not seen finish.
 */
const invalidateAfterWrite = (
  queryClient: QueryClient,
  key: QueryKey,
  predicate?: (query: Query) => boolean,
): Promise<void> =>
  queryClient.invalidateQueries(key, predicate ? { predicate } : {}, {
    cancelRefetch: true,
  });

/**
 * Refetch the cached revision chains a terminal status event touched: any
 * view of the event's chain, the views keyed by its two records (the new
 * revision's own view still holds its pre-publish chain), and views holding
 * no data. Without a chain id, all of them.
 */
const invalidateRevisionChain = (
  queryClient: QueryClient,
  ev: SDRStatusEvent,
): Promise<void> => {
  const chainId = ev.chain_id;
  if (chainId == null) {
    return invalidateAfterWrite(queryClient, ["record-revisions"]);
  }
  const touched = [ev.sdr_id, ev.supersedes_sdr_id];
  return invalidateAfterWrite(queryClient, ["record-revisions"], (query) => {
    const data = query.state.data as RevisionChainResponse | undefined;
    return (
      data === undefined ||
      data.chain_id === chainId ||
      touched.includes(query.queryKey[1] as number)
    );
  });
};

interface FolderPage {
  items: ProjectFolder[];
  total: number;
}

// === LIST FOLDERS ===
const getFolderPage = (
  parentId: number | undefined,
  offset: number,
): Promise<FolderPage> => {
  const params: Record<string, number> = {
    limit: FOLDER_PAGE_SIZE,
    offset,
  };
  if (parentId !== undefined) params.parent_id = parentId;
  return axios
    .get(`${nuclearn_api_url}/project-folders/`, { params })
    .then((res) => ({ items: res.data.items, total: res.data.total ?? 0 }));
};

/**
 * Paginated folder listing. Returns the flattened set of folders loaded so
 * far (so every existing consumer keeps reading `data` as a folder array),
 * plus `fetchNextPage`/`hasNextPage`/`isFetchingNextPage` to drive a
 * load-more affordance and `total` for the full count.
 *
 * `undefined` lists the root; `null` fetches nothing. The two are distinct
 * because a collapsed tree node has no children to ask for yet, and passing
 * `undefined` for that subscribed it to the *root* listing instead — every
 * collapsed node an extra observer of one query, each able to trigger a
 * background refetch once `staleTime` lapsed. That is what made #2454's
 * write/read race a coin flip rather than a rarity. `useFolder` and
 * `useFolderFiles` already spell "not yet" as `null`.
 */
export const useFolders = (parentId?: number | null) => {
  const queryClient = useQueryClient();
  const enabled = parentId !== null;
  const query = useInfiniteQuery(
    ["project-folders", parentId ?? "root"],
    ({ pageParam = 0 }) => getFolderPage(parentId ?? undefined, pageParam),
    {
      enabled,
      refetchOnWindowFocus: false,
      // M6.5.2: keep responses fresh for 30s so navigating between folders
      // doesn't refetch the same parent list on each click. SSE
      // invalidations still bust the cache on real permission changes.
      staleTime: 30_000,
      // Next page starts at the count already loaded; stop once we've loaded
      // `total`. Server order is stable (name, id), so offset paging is safe.
      getNextPageParam: (_lastPage, pages) => {
        const loaded = pages.reduce((n, p) => n + p.items.length, 0);
        const total = pages[0]?.total ?? 0;
        return loaded < total ? loaded : undefined;
      },
      // Seed each listed child into the singular ["project-folder", id] cache
      // so drilling into a row resolves useFolder() (and the derived
      // breadcrumb chain) instantly, with no per-click round-trip flash.
      onSuccess: (data) => {
        data.pages.forEach((page) =>
          page.items.forEach((f) => {
            queryClient.setQueryData(["project-folder", f.id], f);
          }),
        );
      },
    },
  );

  // A disabled hook shares the root key, so it must not read the root list
  // out of the cache and report it as this folder's children.
  const folders = enabled
    ? (query.data?.pages.flatMap((p) => p.items) ?? [])
    : [];
  const total = enabled ? (query.data?.pages[0]?.total ?? folders.length) : 0;
  return { ...query, data: folders, total };
};

// === GET SINGLE FOLDER ===
const getFolder = (id: number): Promise<ProjectFolder> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}`)
    .then((res) => res.data);
};

export const useFolder = (id: number | null) => {
  // Singular key — must not collide with useFolders' list cache. Sharing
  // ["project-folders", N] between an array result (children of N) and an
  // object result (folder N itself) is what produced the
  // (folders || []).filter-is-not-a-function crash, since react-query
  // identifies queries by key, not by hook.
  return useQuery(["project-folder", id], () => getFolder(id!), {
    enabled: id !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    // A 4xx (forbidden / not found) won't recover on retry; only retry
    // transient errors, and stop hammering an endpoint that just denied us.
    retry: (failureCount, error) => {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
  });
};

// === CREATE PROJECT (root folder) ===
// The only root-mint path (spec §5.8, Q4): POST /project-folders/ requires
// a parent_id, so a root can only come from here.
const createProject = (data: CreateProjectRequest): Promise<ProjectFolder> => {
  return axios
    .post(`${nuclearn_api_url}/project-folders/projects`, data)
    .then((res) => res.data);
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation(createProject, {
    onSuccess: (data) => {
      captureEvent(AA_EVENTS.FOLDER_CREATED, {
        folder_id: data.id,
        parent_id: null,
      });
      invalidateAfterWrite(queryClient, ["project-folders", "root"]);
    },
  });
};

// === CREATE FOLDER ===
const createFolder = (data: CreateFolderRequest): Promise<ProjectFolder> => {
  return axios
    .post(`${nuclearn_api_url}/project-folders/`, data)
    .then((res) => res.data);
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(createFolder, {
    onSuccess: (data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_CREATED, {
        folder_id: data.id,
        parent_id: variables.parent_id,
      });
      invalidateAfterWrite(queryClient, [
        "project-folders",
        variables.parent_id,
      ]);
    },
  });
};

// === UPDATE FOLDER ===
// `oldParent` is the folder's parent BEFORE this update; we need it
// explicitly because `data.parent_id` is the *new* desired parent (or
// undefined), and the cache-invalidation step has to bust both lists.
// Callers that don't intend to move the folder should still pass the
// current parent so the parent's list is invalidated for rename refreshes.
const updateFolder = ({
  id,
  data,
}: {
  id: number;
  data: UpdateFolderRequest;
  oldParent?: number | null;
}): Promise<ProjectFolder> => {
  return axios
    .patch(`${nuclearn_api_url}/project-folders/${id}`, data)
    .then((res) => res.data);
};

export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(updateFolder, {
    onSuccess: (data, variables) => {
      invalidateAfterWrite(queryClient, ["project-folder", variables.id]);
      const oldParent = variables.oldParent ?? null;
      invalidateAfterWrite(queryClient, [
        "project-folders",
        oldParent ?? "root",
      ]);
      if (data?.parent_id !== oldParent) {
        invalidateAfterWrite(queryClient, [
          "project-folders",
          data?.parent_id ?? "root",
        ]);
      }
    },
  });
};

// === DELETE FOLDER ===
const deleteFolder = ({
  id,
}: {
  id: number;
  parent_id?: number | null;
}): Promise<void> => {
  return axios.delete(`${nuclearn_api_url}/project-folders/${id}`);
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteFolder, {
    onSuccess: (_data, variables) => {
      invalidateAfterWrite(queryClient, ["project-folder", variables.id]);
      invalidateAfterWrite(queryClient, [
        "project-folders",
        variables.parent_id ?? "root",
      ]);
    },
  });
};

// === RESTORE FOLDER (undo soft-delete before the purge runs) ===
// Owner only. 409 means the purge task already started; the folder is gone.
const restoreFolder = ({
  id,
}: {
  id: number;
  parent_id?: number | null;
}): Promise<ProjectFolder> => {
  return axios
    .post(`${nuclearn_api_url}/project-folders/${id}/restore`)
    .then((res) => res.data);
};

export const useRestoreFolder = () => {
  const queryClient = useQueryClient();
  return useMutation(restoreFolder, {
    onSuccess: (data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_RESTORED, { folder_id: variables.id });
      invalidateAfterWrite(queryClient, ["project-folder", variables.id]);
      invalidateAfterWrite(queryClient, [
        "project-folders",
        variables.parent_id ?? data?.parent_id ?? "root",
      ]);
    },
  });
};

// === TRANSFER OWNERSHIP (admin only) ===
// Repoints the owner grant to the successor; returns the successor's share
// row. Content-blind: the acting admin gains no access (spec §5.8, Q2).
const transferOwnership = ({
  folderId,
  data,
}: {
  folderId: number;
  parent_id?: number | null;
  data: TransferOwnershipRequest;
}): Promise<UserShareInfo> => {
  return axios
    .post(
      `${nuclearn_api_url}/project-folders/${folderId}/transfer-ownership`,
      data,
    )
    .then((res) => res.data);
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();
  return useMutation(transferOwnership, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_OWNERSHIP_TRANSFERRED, {
        folder_id: variables.folderId,
        new_owner_user_id: variables.data.new_owner_user_id,
      });
      invalidateSharedFolder(
        queryClient,
        variables.folderId,
        variables.parent_id,
      );
    },
  });
};

// === GET FOLDER SHARES ===
// Owner-only: 403 for everyone else.
const getFolderShares = (id: number): Promise<FolderSharesResponse> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}/shares`)
    .then((res) => res.data);
};

// M6.5.2 staleTime added uniformly to every hot read-side hook below.
export const useFolderShares = (id: number | null, enabled = true) => {
  return useQuery(["project-folder-shares", id], () => getFolderShares(id!), {
    enabled: enabled && id !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === BATCH FOLDER SHARES (MRE#30776) ===
// Shares for every *owned* folder in one round trip, instead of one call per
// folder — the endpoint is still owner-gated per id, but a non-owned id is
// silently absent from the response rather than 403ing the whole request, so
// callers only ever send ids they already know (via ancestor
// `permission_level`) they own.
const getFolderSharesBatch = (
  folderIds: number[],
): Promise<Record<string, FolderSharesResponse>> => {
  return axios
    .post(`${nuclearn_api_url}/project-folders/shares/batch`, {
      folder_ids: folderIds,
    })
    .then((res) => res.data.items);
};

// Sorted so the query key (and cache hit/miss) is insensitive to the order
// ancestors happen to be enumerated in — the same set of owned ids should
// resolve as one cache entry, not a new one per ordering.
const folderSharesBatchKey = (folderIds: number[]) => [
  "project-folder-shares",
  "batch",
  [...folderIds].sort((a, b) => a - b),
];

// D.3 helper: invalidate exactly the caches a share change can affect —
// the folder's shares list, the folder's own row (for permission_level),
// its parent's child-list, and any active batch fetch (any owned-ancestor
// set could include this folder). SSE handles the cross-user case for the
// grantee separately, so we don't need to invalidate every cached row.
const invalidateSharedFolder = (
  queryClient: ReturnType<typeof useQueryClient>,
  folderId: number,
  parentId?: number | null,
) => {
  invalidateAfterWrite(queryClient, ["project-folder-shares", folderId]);
  // Prefix-only (no id list): the batch key's id set is whatever the caller
  // happened to request, so this can't target one folder specifically —
  // busting every active batch query is the cheap, correct trade (at most
  // one is ever active per open dialog).
  invalidateAfterWrite(queryClient, ["project-folder-shares", "batch"]);
  invalidateAfterWrite(queryClient, ["project-folder", folderId]);
  invalidateAfterWrite(queryClient, ["project-folders", parentId ?? "root"]);
};

// === ADD USER SHARE ===
const addUserShare = ({
  folderId,
  data,
}: {
  folderId: number;
  parent_id?: number | null;
  data: {
    user_id: number;
    permission_level: string;
    recursive?: boolean;
  };
}): Promise<void> => {
  return axios.post(
    `${nuclearn_api_url}/project-folders/${folderId}/shares/user`,
    data,
  );
};

export const useAddUserShare = () => {
  const queryClient = useQueryClient();
  return useMutation(addUserShare, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_SHARE_GRANTED, {
        folder_id: variables.folderId,
        share_type: "user",
        share_target: variables.data.user_id,
        permission_level: variables.data.permission_level,
        recursive: variables.data.recursive ?? false,
      });
      invalidateSharedFolder(
        queryClient,
        variables.folderId,
        variables.parent_id,
      );
    },
  });
};

// === ADD GROUP SHARE ===
const addGroupShare = ({
  folderId,
  data,
}: {
  folderId: number;
  parent_id?: number | null;
  data: {
    group_id: number;
    permission_level: string;
    recursive?: boolean;
  };
}): Promise<void> => {
  return axios.post(
    `${nuclearn_api_url}/project-folders/${folderId}/shares/group`,
    data,
  );
};

export const useAddGroupShare = () => {
  const queryClient = useQueryClient();
  return useMutation(addGroupShare, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_SHARE_GRANTED, {
        folder_id: variables.folderId,
        share_type: "group",
        share_target: variables.data.group_id,
        permission_level: variables.data.permission_level,
        recursive: variables.data.recursive ?? false,
      });
      invalidateSharedFolder(
        queryClient,
        variables.folderId,
        variables.parent_id,
      );
      // Sharing a folder with a group changes that group's "used on N folders"
      // view (groupService useGroupFolderGrants) — refresh it so the group
      // detail modal isn't stale after a share.
      invalidateAfterWrite(queryClient, [
        "groups",
        variables.data.group_id,
        "folder-grants",
      ]);
    },
  });
};

// === REMOVE USER SHARE ===
const removeUserShare = ({
  folderId,
  userId,
}: {
  folderId: number;
  userId: number;
  parent_id?: number | null;
}): Promise<void> => {
  return axios.delete(
    `${nuclearn_api_url}/project-folders/${folderId}/shares/user/${userId}`,
  );
};

export const useRemoveUserShare = () => {
  const queryClient = useQueryClient();
  return useMutation(removeUserShare, {
    onSuccess: (_data, variables) => {
      invalidateSharedFolder(
        queryClient,
        variables.folderId,
        variables.parent_id,
      );
      captureEvent(AA_EVENTS.FOLDER_SHARE_REVOKED, {
        folder_id: variables.folderId,
        share_type: "user",
        share_target: variables.userId,
      });
    },
  });
};

// === REMOVE GROUP SHARE ===
const removeGroupShare = ({
  folderId,
  groupId,
}: {
  folderId: number;
  groupId: number;
  parent_id?: number | null;
}): Promise<void> => {
  return axios.delete(
    `${nuclearn_api_url}/project-folders/${folderId}/shares/group/${groupId}`,
  );
};

export const useRemoveGroupShare = () => {
  const queryClient = useQueryClient();
  return useMutation(removeGroupShare, {
    onSuccess: (_data, variables) => {
      invalidateSharedFolder(
        queryClient,
        variables.folderId,
        variables.parent_id,
      );
      captureEvent(AA_EVENTS.FOLDER_SHARE_REVOKED, {
        folder_id: variables.folderId,
        share_type: "group",
        share_target: variables.groupId,
      });
      // Revoking a group's share changes that group's "used on N folders"
      // view (groupService useGroupFolderGrants) — refresh it so the group
      // detail modal isn't stale after a revoke.
      invalidateAfterWrite(queryClient, [
        "groups",
        variables.groupId,
        "folder-grants",
      ]);
    },
  });
};

// Which records in a folder a prospective recipient still could not open
// after the share is granted (spec §5.6). Errors propagate, including the
// 404 the endpoint returns for a folder that is unreadable or gone: turning
// that into an empty result would make "the check could not run" and
// "nothing to disclose" indistinguishable, and a frontend deployed ahead of
// this endpoint would report no gaps for every share.
const getAccessGap = (
  folderId: number,
  request: AccessGapRequest,
): Promise<AccessGapResponse> => {
  return axios
    .post(`${nuclearn_api_url}/project-folders/${folderId}/access-gap`, request)
    .then((res) => res.data);
};

/**
 * Access-gap disclosure for a mounted view.
 *
 * `isError` means the disclosure could not be computed — render "couldn't
 * verify", never "no gaps". The gap is a permissions warning, so a silent
 * empty state on failure tells the sharer the opposite of the truth.
 */
export const useAccessGap = (
  folderId: number | null,
  userIds: number[],
  groupIds: number[],
  enabled = true,
  // Scope of the share being previewed. Part of the cache key: the two scopes
  // are different answers, so toggling must not reuse the other one's.
  recursive = true,
) => {
  // Sorted copies so the cache key is insensitive to staging order.
  const users = [...userIds].sort((a, b) => a - b);
  const groups = [...groupIds].sort((a, b) => a - b);
  return useQuery(
    ["project-folder-access-gap", folderId, users, groups, recursive],
    () =>
      getAccessGap(folderId!, {
        user_ids: users,
        group_ids: groups,
        recursive,
      }),
    {
      enabled: enabled && folderId !== null && users.length + groups.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: retryUnlessClientError,
    },
  );
};

// The same disclosure for one-shot flows (after adding a record to a shared
// project) rather than a mounted view. One transport, two call shapes: #2388
// reads it through the hook, #2400 awaits it inline.
export const computeFolderAccessGap = getAccessGap;

// === GET FOLDER FILES ===
const getFolderFiles = (id: number): Promise<ProjectFile[]> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}/files`, {
      params: { limit: LIST_HARD_CAP },
    })
    .then((res) => res.data.files);
};

export const useFolderFiles = (id: number | null, enabled = true) => {
  return useQuery(["project-folder-files", id], () => getFolderFiles(id!), {
    enabled: enabled && id !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === FOLDER PROCESSING STATUS ===
// Per-file index/processing state plus a folder rollup. Live updates ride
// the per-SDR SSE stream (no polling): we subscribe to every folder file's
// notify:sdr:{id}:status key and patch the cache in place. New uploads enter
// via the upload mutation's cache invalidation, which refetches the set and
// re-subscribes to the new key.
const EMPTY_SUMMARY: FolderProcessingSummary = {
  total: 0,
  processing: 0,
  completed: 0,
  failed: 0,
};

const isCompletedStatus = (status: string | null): boolean =>
  status === "completed" || status === "no_processing_needed";

const computeSummary = (
  files: ProjectFileProcessingStatus[],
): FolderProcessingSummary => {
  const summary: FolderProcessingSummary = {
    total: files.length,
    processing: 0,
    completed: 0,
    failed: 0,
  };
  for (const f of files) {
    if (f.is_processing) summary.processing += 1;
    else if (isCompletedStatus(f.status)) summary.completed += 1;
    else summary.failed += 1;
  }
  return summary;
};

// Adapt one file's state to the shared DocumentStatusIndicator input shape.
const toProcessingStatusData = (
  f: ProjectFileProcessingStatus,
): ProcessingStatusData => ({
  source_data_record_id: f.sdr_id,
  dataset_id: 0,
  filename: f.name,
  status: f.status ?? "",
  display_status: f.display_status ?? "",
  is_processing: f.is_processing,
  error_message: f.error_message,
  progress_percentage: f.progress_percentage,
  num_chunks: f.num_chunks ?? 0,
  num_chunks_embedded: f.num_chunks_ready ?? 0,
  num_chunks_ready: f.num_chunks_ready ?? undefined,
  uploaded_at: null,
  last_updated: null,
  is_retryable: f.is_retryable,
});

const getFolderProcessingStatus = (
  id: number,
): Promise<FolderProcessingStatusResponse> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}/processing-status`)
    .then((res) => res.data);
};

// Statuses publish_current_status_sync's terminal-success gate will never
// republish for; a replay of one has nothing new to report regardless of
// whether a title already landed before a later stage failed.
const NO_TITLE_COMING_STATUSES = new Set([
  ...FAILURE_STATUSES,
  "cancelled",
  "suspected_orphan",
  "no_processing_needed", // folder images: extraction never runs on these
]);

// Debounces per-folder title-refetch invalidations across every mounted
// useFolderProcessingStatus instance into one invalidation per folder per
// window. A plain queueMicrotask only coalesces events in the same SSE
// network chunk, not a whole reconnect's replay burst (fetch-event-source
// dispatches once per chunk, with an await between chunks).
const TITLE_REFETCH_DEBOUNCE_MS = 100;
const pendingTitleRefetchFolderIds = new WeakMap<QueryClient, Set<number>>();

const scheduleTitleRefetch = (
  queryClient: QueryClient,
  folderId: number,
): void => {
  let pending = pendingTitleRefetchFolderIds.get(queryClient);
  if (!pending) {
    pending = new Set<number>();
    pendingTitleRefetchFolderIds.set(queryClient, pending);
  }
  const needsSchedule = pending.size === 0;
  pending.add(folderId);
  if (!needsSchedule) return;
  setTimeout(() => {
    const ids = Array.from(pending);
    pending.clear();
    for (const id of ids) {
      invalidateAfterWrite(queryClient, ["project-folder-files", id]);
    }
  }, TITLE_REFETCH_DEBOUNCE_MS);
};

export const useFolderProcessingStatus = (
  folderId: number | null,
  enabled = true,
) => {
  const queryClient = useQueryClient();
  const queryKey = ["folder-processing-status", folderId];

  const query = useQuery(queryKey, () => getFolderProcessingStatus(folderId!), {
    enabled: enabled && folderId !== null,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Subscribe to every folder file's per-SDR status stream at once.
  const streamKeys = useMemo(
    () => (query.data?.files ?? []).map((f) => `notify:sdr:${f.sdr_id}:status`),
    [query.data],
  );

  useNotificationStreams(streamKeys, "sdr_status", (raw) => {
    const ev = raw as unknown as SDRStatusEvent;
    // A publish only grows the chain and repoints references when processing
    // reaches a terminal state, so that transition — not the upload — is when
    // the revision caches are stale. Captured here and acted on below, because
    // the setQueryData updater must stay a pure function of prev.
    let finishedProcessing = false;
    queryClient.setQueryData<FolderProcessingStatusResponse | undefined>(
      queryKey,
      (prev) => {
        if (!prev) return prev;
        const idx = prev.files.findIndex((f) => f.sdr_id === ev.sdr_id);
        if (idx === -1) return prev;
        const old = prev.files[idx];
        const updated: ProjectFileProcessingStatus = {
          ...old,
          status: ev.status ?? old.status,
          display_status: ev.display_status ?? old.display_status,
          is_processing:
            ev.is_processing !== undefined
              ? ev.is_processing
              : old.is_processing,
          progress_percentage:
            ev.progress_percentage ?? old.progress_percentage,
          // Explicit undefined check so a backend null (error cleared) applies.
          error_message:
            ev.error_message !== undefined
              ? ev.error_message
              : old.error_message,
        };
        if (
          updated.status === old.status &&
          updated.display_status === old.display_status &&
          updated.is_processing === old.is_processing &&
          updated.progress_percentage === old.progress_percentage &&
          updated.error_message === old.error_message
        ) {
          return prev;
        }
        finishedProcessing =
          old.is_processing === true && !updated.is_processing;
        const files = prev.files.slice();
        files[idx] = updated;
        return { ...prev, files, summary: computeSummary(files) };
      },
    );

    if (finishedProcessing) {
      invalidateRevisionChain(queryClient, ev);
      if (folderId !== null) {
        invalidateAfterWrite(queryClient, ["folder-references", folderId]);
        // useFolderFiles' own query key. Debounced like the branch below:
        // the chip and contents-list instances both run setQueryData on one
        // dispatch, so only one sees finishedProcessing true and the other
        // falls into that branch instead for the same completion.
        scheduleTitleRefetch(queryClient, folderId);
      }
    } else if (
      folderId !== null &&
      ev.is_processing === false &&
      !NO_TITLE_COMING_STATUSES.has(ev.status)
    ) {
      // Metadata extraction can commit a title well after the SDR went
      // terminal; publish_current_status_sync re-publishes the (unchanged)
      // status so this branch gets a nudge, which the equality check above
      // alone would otherwise treat as a no-op.
      const files = queryClient.getQueryData<ProjectFile[]>([
        "project-folder-files",
        folderId,
      ]);
      const file = files?.find((f) => f.sdr_id === ev.sdr_id);
      // record_title falls back to the file name, so a title-less file
      // reads as title === name, not null (NO_TITLE_COMING_STATUSES is what
      // stops those from re-invalidating forever). An absent file/field
      // falls through and still invalidates, since there's nothing to compare.
      const nothingToFix =
        file !== undefined &&
        file.title !== undefined &&
        (file.title === null || file.title !== file.name);
      if (!nothingToFix) {
        scheduleTitleRefetch(queryClient, folderId);
      }
    }
  });

  // Refetch once on SSE reconnect so a missed event can't strand the view.
  useSSEFallbackInvalidation(queryClient, queryKey, folderId !== null);

  const byName = useMemo(() => {
    const map: Record<string, ProcessingStatusData> = {};
    for (const f of query.data?.files ?? []) {
      map[f.name] = toProcessingStatusData(f);
    }
    return map;
  }, [query.data]);

  return {
    byName,
    summary: query.data?.summary ?? EMPTY_SUMMARY,
    isLoading: query.isLoading,
  };
};

// === DOWNLOAD FILE AS TEXT ===
// Raw bytes of a folder file as text, for client-side parsing (e.g. the CSV
// grid viewer). Hits the same /download endpoint the row download uses, just
// reading the body as text instead of a blob. No indexing/SDR dependency —
// text files are served straight from project-files.
const getFolderFileText = (
  folderId: number,
  fileName: string,
): Promise<string> => {
  return axios
    .get<string>(
      `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(
        fileName,
      )}/download`,
      { responseType: "text" },
    )
    .then((res) => res.data);
};

export const useFolderFileText = (
  folderId: number | null,
  fileName: string | null,
  enabled = true,
) => {
  return useQuery(
    ["project-folder-file-text", folderId, fileName],
    () => getFolderFileText(folderId!, fileName!),
    {
      enabled: enabled && folderId !== null && !!fileName,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === DOWNLOAD FILE AS BLOB ===
// Raw bytes of a folder file as a Blob, for client-side rendering (e.g. the
// image viewer). Same /download endpoint as the text/blob row download; no
// indexing/SDR dependency.
const getFolderFileBlob = (
  folderId: number,
  fileName: string,
): Promise<Blob> => {
  return axios
    .get<Blob>(
      `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(
        fileName,
      )}/download`,
      { responseType: "blob" },
    )
    .then((res) => res.data);
};

export const useFolderFileBlob = (
  folderId: number | null,
  fileName: string | null,
  enabled = true,
) => {
  return useQuery(
    ["project-folder-file-blob", folderId, fileName],
    () => getFolderFileBlob(folderId!, fileName!),
    {
      enabled: enabled && folderId !== null && !!fileName,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === FILE SDR RECORD + CHUNKS (rich document viewer) ===
// Folder-scoped reads of a file's indexed SDR and its content chunks, gated by
// the folder resolver (not the dataset role gate). These feed the shared
// DocumentViewerCore body so a project file renders like a dataset document.
const getFolderFileRecord = (
  folderId: number,
  fileName: string,
): Promise<SourceDataRecord> => {
  return axios
    .get<SourceDataRecord>(
      `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(
        fileName,
      )}/record`,
    )
    .then((res) => res.data);
};

export const useFolderFileRecord = (
  folderId: number | null,
  fileName: string | null,
  enabled = true,
) => {
  return useQuery(
    ["project-folder-file-record", folderId, fileName],
    () => getFolderFileRecord(folderId!, fileName!),
    {
      enabled: enabled && folderId !== null && !!fileName,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      staleTime: 30_000,
    },
  );
};

const getFolderFileChunks = (
  folderId: number,
  fileName: string,
): Promise<DocumentChunk[]> => {
  return axios
    .get<
      DocumentChunk[]
    >(`${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(fileName)}/chunks`)
    .then((res) => res.data);
};

export const useFolderFileChunks = (
  folderId: number | null,
  fileName: string | null,
  enabled = true,
) => {
  return useQuery(
    ["project-folder-file-chunks", folderId, fileName],
    () => getFolderFileChunks(folderId!, fileName!),
    {
      enabled: enabled && folderId !== null && !!fileName,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30_000,
    },
  );
};

// === UPLOAD FILE ===
/**
 * Upload one file to a folder. Files over CHUNKED_UPLOAD_THRESHOLD go in
 * chunks: progress moves in chunk steps, and a failure rejects with an Error
 * whose message is user-facing and whose `response` is the server's, instead
 * of a raw AxiosError.
 */
const uploadFile = async ({
  folderId,
  file,
  supersedesSdrId,
  revisionLabel,
  onProgress,
}: {
  folderId: number;
  file: File;
  // Publish-new-version (Library spec §5): the upload lands as a new SDR and
  // is chained onto this record — and the project's references repointed —
  // only on terminal processing success.
  supersedesSdrId?: number;
  revisionLabel?: string;
  onProgress?: (progress: number) => void;
}): Promise<FileUploadResponse> => {
  const publishFields: Record<string, string> = {};
  if (supersedesSdrId != null) {
    publishFields.supersedes_sdr_id = String(supersedesSdrId);
  }
  if (revisionLabel != null) {
    publishFields.revision_label = revisionLabel;
  }

  // An edge proxy caps one request body (Cloudflare answers 413), so large
  // files go in chunks of CHUNKED_UPLOAD_THRESHOLD, which must stay under
  // that cap. Every chunk carries the publish fields: the route checks them
  // on each chunk, so a bad target fails on chunk 0, and acts on the last.
  if (file.size > CHUNKED_UPLOAD_THRESHOLD) {
    const response = await uploadFileInChunks({
      context: "project-folder",
      contextId: String(folderId),
      file,
      extraFields: publishFields,
      onProgress: (progress) => onProgress?.(Math.round(progress)),
    });
    return response.result as FileUploadResponse;
  }

  const formData = new FormData();
  formData.append("file", file);
  Object.entries(publishFields).forEach(([name, value]) =>
    formData.append(name, value),
  );

  return axios
    .post(`${nuclearn_api_url}/project-folders/${folderId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    })
    .then((res) => res.data);
};

export const useUploadFile = () => {
  const queryClient = useQueryClient();
  return useMutation(uploadFile, {
    onSuccess: (_data, variables) => {
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.folderId,
      ]);
      // No revision-chain invalidation here: at upload time the publish has
      // not grown the chain or repointed anything yet. useFolderProcessingStatus
      // busts those caches when the SDR reaches a terminal state, which is when
      // they actually change.
    },
  });
};

// === DELETE FILE ===
const deleteFile = ({
  folderId,
  fileName,
}: {
  folderId: number;
  fileName: string;
}): Promise<void> => {
  return axios.delete(
    `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(fileName)}`,
  );
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteFile, {
    onSuccess: (_data, variables) => {
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.folderId,
      ]);
    },
  });
};

// === MOVE FILE (between two writable folders) ===
const moveFile = ({
  folderId,
  fileName,
  data,
}: {
  folderId: number;
  fileName: string;
  data: MoveFileRequest;
}): Promise<FileUploadResponse> => {
  return axios
    .patch(
      `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(fileName)}`,
      data,
    )
    .then((res) => res.data);
};

export const useMoveFile = () => {
  const queryClient = useQueryClient();
  return useMutation(moveFile, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_FILE_MOVED, {
        folder_id: variables.folderId,
        destination_folder_id: variables.data.destination_folder_id,
        filename: variables.fileName,
      });
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.data.destination_folder_id,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.data.destination_folder_id,
      ]);
    },
  });
};

// === GET FOLDER ANCESTORS (for breadcrumbs) ===
const getFolderAncestors = (id: number): Promise<ProjectFolderAncestor[]> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}/ancestors`)
    .then((res) => res.data.items);
};

export const useFolderAncestors = (id: number | null) => {
  return useQuery(
    ["project-folder-ancestors", id],
    () => getFolderAncestors(id!),
    {
      enabled: id !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === ANCESTOR SHARES (share dialog "inherited from X" entries) ===
export interface AncestorSharesResult {
  ancestor: ProjectFolderAncestor;
  // undefined while loading and for ancestors the viewer cannot administer
  // (the shares endpoint is owner-only and 403s).
  shares?: FolderSharesResponse;
  // Set when this ancestor's query failed (e.g. the 403 above). Callers that
  // derive a disclosure from `shares` need this to know the gap is a failed
  // lookup rather than "this ancestor truly has no recursive grants".
  error?: unknown;
  // True until this ancestor's query has settled (succeeded or failed).
  // `shares === undefined` alone doesn't distinguish "still loading" from
  // "resolved to nothing to report" — callers that gate a confident answer
  // on every ancestor need this to tell the two apart.
  isLoading: boolean;
}

// Synthesized in place of a live 403 for an ancestor useAncestorShares has
// positive evidence it doesn't own — `permission_level` present and not
// "owner" (MRE#30776), or (the batch case below) the server's own
// authorization check disagreeing with a stale "owner" belief after the
// fact. Exported so callers can identify it by reference rather than by
// status code: unlike the live 403 it replaces, a real network failure
// (e.g. the batch endpoint's own capability gate rejecting the request) can
// now also carry status 403 without meaning the same thing. Not a real
// AxiosError, just duck-typed to satisfy `axios.isAxiosError`, which only
// checks `.isAxiosError === true`.
export const NOT_OWNED_ANCESTOR_ERROR = {
  isAxiosError: true,
  response: { status: 403 },
};

// Synthesized for an ancestor with no `permission_level` at all — absence of
// evidence, not evidence of non-ownership (an older cached `/ancestors`
// response, or a genuinely ungranted ancestor look the same). Callers that
// distinguish "confirmed not owned" from "never found out" key off this
// being a different object than NOT_OWNED_ANCESTOR_ERROR; both still read as
// "no shares available" to anything that only checks `error != null`.
export const UNKNOWN_ANCESTOR_LEVEL_ERROR = {
  isAxiosError: true,
  response: { status: 403 },
};

// Inherited access is computed, never materialized (spec §5.3), so the
// share dialog derives "inherited from X" entries from each ancestor's
// recursive grants. Ancestors the viewer does not own contribute nothing:
// `/ancestors` already tells us which ones via `permission_level`, so those
// skip the network entirely instead of asking and reading back the
// guaranteed 403 — an inherited row is only shown where "revoke at source"
// is actionable anyway, i.e. exactly the owned ones. A `permission_level`
// present and not "owner" is confirmed non-ownership; missing entirely (an
// older cached response) is absence of evidence, not evidence of absence —
// callers that need to tell the two apart (e.g. an affirmative "this is
// shared" claim) key off which error object comes back, not just whether
// one did.
//
// The owned subset is fetched in one batch call rather than one request per
// ancestor (MRE#30776) — a real chain can be owned end-to-end, and firing N
// legitimate requests for it was the other half of the fan-out this issue
// named, not just the guaranteed-403 half. `error`/`isLoading` are the same
// object across every owned entry: the batch either succeeds or fails as a
// unit, which every existing caller's `.some(r => r.error)` already treats
// as an OR across entries anyway.
export const useAncestorShares = (
  ancestors: ProjectFolderAncestor[] | undefined,
  enabled = true,
): AncestorSharesResult[] => {
  const list = enabled ? (ancestors ?? []) : [];
  const ownedIds = list
    .filter((a) => a.permission_level === "owner")
    .map((a) => a.id);

  const batch = useQuery(
    folderSharesBatchKey(ownedIds),
    () => getFolderSharesBatch(ownedIds),
    {
      // No owned ancestors means nothing this endpoint could ever return —
      // skip the call rather than send a request `folder_ids: []` would 422.
      enabled: ownedIds.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Matches the per-ancestor calls this replaced: a genuine failure here
      // should surface (and let the dialog's caveat/error state settle)
      // promptly rather than retrying and leaving the disclosure pending.
      retry: false,
    },
  );

  // Memoized so a caller (useInheritedRecursiveShares) that keys its own
  // useMemo off this array's identity actually gets a cache hit across
  // renders that don't change any input, instead of recomputing every time
  // off a fresh array this function would otherwise return unconditionally.
  return useMemo(
    () =>
      list.map((a) => {
        if (a.permission_level === undefined) {
          return {
            ancestor: a,
            shares: undefined,
            error: UNKNOWN_ANCESTOR_LEVEL_ERROR,
            isLoading: false,
          };
        }
        if (a.permission_level !== "owner") {
          return {
            ancestor: a,
            shares: undefined,
            error: NOT_OWNED_ANCESTOR_ERROR,
            isLoading: false,
          };
        }
        // The batch endpoint silently omits an id its own authorization
        // check rejects (MRE#30776) — normally impossible for an id we
        // only sent because permission_level said "owner", but a race
        // (ownership revoked between the /ancestors fetch and this one)
        // or a stale cache can still produce it. Treating that gap as "no
        // recursive grants" would be a confident wrong answer, exactly the
        // failure mode every existing consumer's error handling exists to
        // avoid — so a settled, errorless response missing this id gets
        // the same not-owned shape a live 403 would have, instead of
        // silently reading as empty.
        const missingFromResponse =
          !batch.isLoading &&
          batch.error == null &&
          batch.data !== undefined &&
          !(a.id in batch.data);
        return {
          ancestor: a,
          shares: batch.data?.[a.id],
          error: missingFromResponse ? NOT_OWNED_ANCESTOR_ERROR : batch.error,
          isLoading: batch.isLoading,
        };
      }),
    [list, batch.data, batch.error, batch.isLoading],
  );
};

/** One recursive grant plus the ancestor it was found on. */
export interface InheritedShare<T> {
  share: T;
  source: ProjectFolderAncestor;
}

/**
 * Flattens `useAncestorShares`' per-ancestor results to the recursive-only
 * grants that reach a descendant (spec §5.3), split by subject kind.
 *
 * Both `CreateFolderDialog` (who will read a new child) and
 * `ShareFolderDialog` (what does this folder inherit) derive the same rule —
 * a recursive grant on this list's entries reaches down — from the same
 * `useAncestorShares` result; this is the one place that rule is expressed
 * (#2461). Callers that also need to fold in the target folder's own shares
 * (not just its ancestors') can prepend a pseudo-ancestor entry for it before
 * calling this — the recursive filter applies identically either way.
 */
export const useInheritedRecursiveShares = (
  ancestorShares: AncestorSharesResult[],
): {
  userShares: InheritedShare<UserShareInfo>[];
  groupShares: InheritedShare<GroupShareInfo>[];
} => {
  return useMemo(
    () => ({
      userShares: ancestorShares.flatMap(({ ancestor, shares }) =>
        (shares?.user_shares ?? [])
          .filter((share) => share.recursive)
          .map((share) => ({ share, source: ancestor })),
      ),
      groupShares: ancestorShares.flatMap(({ ancestor, shares }) =>
        (shares?.group_shares ?? [])
          .filter((share) => share.recursive)
          .map((share) => ({ share, source: ancestor })),
      ),
    }),
    [ancestorShares],
  );
};

// === RECIPIENTS OF A FOLDER'S SHARE (§5.3) ===

/** Everyone a folder's records reach: direct grants plus recursive ancestor ones. */
export interface FolderRecipients {
  userIds: number[];
  groupIds: number[];
}

/**
 * Resolves who can reach a folder's contents, for the pre-share access-gap
 * disclosure. Same §5.3 rule useAncestorShares implements declaratively —
 * direct grants, plus ancestors' recursive grants, with non-owner ancestors
 * excluded from the batch request (via `permission_level`) rather than
 * fetched and 403ing.
 *
 * A hook returning a callback rather than a query, because the caller needs
 * this *after* the user picks a folder — inside an event handler, where a hook
 * cannot be called. Going through `fetchQuery` on the same keys as
 * `useFolderShares` / `useFolderAncestors` / `useAncestorShares`' batch query
 * is what keeps the imperative shape from costing the cache: a folder whose
 * shares are already loaded resolves without a request, results populate the
 * cache the share dialog reads, and the SSE bust set applies to both.
 */
export const useFolderRecipients = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (folder: {
      id: number;
      owner_id?: number | null;
    }): Promise<FolderRecipients> => {
      const userIds = new Set<number>();
      const groupIds = new Set<number>();
      const collect = (
        shares: FolderSharesResponse,
        recursiveOnly: boolean,
      ) => {
        shares.user_shares
          .filter((sh) => !recursiveOnly || sh.recursive)
          .filter((sh) => sh.user_id !== folder.owner_id)
          .forEach((sh) => userIds.add(sh.user_id));
        shares.group_shares
          .filter((sh) => !recursiveOnly || sh.recursive)
          .forEach((sh) => groupIds.add(sh.group_id));
      };

      const sharesOf = (id: number) =>
        queryClient.fetchQuery({
          queryKey: ["project-folder-shares", id],
          queryFn: () => getFolderShares(id),
          staleTime: 30_000,
          // A 403 is the expected non-owner outcome, not a transient fault.
          retry: false,
        });

      try {
        collect(await sharesOf(folder.id), false);
      } catch (err) {
        // A 403 is the expected non-owner outcome: no disclosure is available
        // and an empty set is the right answer. Anything else means the check
        // could not run, and the caller has to be able to tell that apart from
        // "nobody is blocked" — swallowing it turned a failed readability
        // check back into a clean "Added X to Y".
        if (!axios.isAxiosError(err) || err.response?.status !== 403) throw err;
      }

      let ancestors: ProjectFolderAncestor[] = [];
      try {
        ancestors = await queryClient.fetchQuery({
          queryKey: ["project-folder-ancestors", folder.id],
          queryFn: () => getFolderAncestors(folder.id),
          staleTime: 30_000,
        });
      } catch {
        // Root folder, or the chain could not be read; direct grants still count.
      }

      // Only an owned ancestor can ever return real shares (owner gate) —
      // fetch just those, in one round trip, instead of firing one call per
      // ancestor and reading back a guaranteed 403 for the rest (MRE#30776;
      // same fix useAncestorShares applies for the mounted-view case, here
      // for this one-shot callback). Same key as useAncestorShares' batch
      // query, so a dialog that already warmed it costs this call nothing.
      const ownedAncestorIds = ancestors
        .filter((a) => a.id !== folder.id && a.permission_level === "owner")
        .map((a) => a.id);
      if (ownedAncestorIds.length > 0) {
        try {
          const batch = await queryClient.fetchQuery({
            queryKey: folderSharesBatchKey(ownedAncestorIds),
            queryFn: () => getFolderSharesBatch(ownedAncestorIds),
            staleTime: 30_000,
            // Best-effort (the catch below swallows a failure) — retrying
            // only delays a result that's discarded anyway.
            retry: false,
          });
          Object.values(batch).forEach((shares) => collect(shares, true));
        } catch {
          // Same best-effort trade the old per-ancestor fan-out made: a
          // failed lookup silently contributes nothing rather than failing
          // the whole recipient resolution over one degraded disclosure.
        }
      }

      return { userIds: [...userIds], groupIds: [...groupIds] };
    },
    [queryClient],
  );
};

// === SEARCH USERS (for share autocomplete) ===
const searchUsers = (
  query: string,
): Promise<
  Array<{ id: number; username: string; user_display_name?: string }>
> => {
  return axios
    .get(`${nuclearn_api_url}/user/search`, { params: { q: query } })
    .then((res) => res.data);
};

export const useSearchUsers = (query: string, enabled = true) => {
  return useQuery(["user-search", query], () => searchUsers(query), {
    enabled: enabled && query.length >= 2,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === ARCHIVE FILE ===
const archiveFile = ({
  folderId,
  fileName,
}: {
  folderId: number;
  fileName: string;
}): Promise<void> => {
  return axios.post(
    `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(fileName)}/archive`,
  );
};

export const useArchiveFile = () => {
  const queryClient = useQueryClient();
  return useMutation(archiveFile, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_FILE_ARCHIVED, {
        folder_id: variables.folderId,
        filename: variables.fileName,
      });
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "project-folder-archived-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.folderId,
      ]);
    },
  });
};

// === RESTORE FILE ===
const restoreFile = ({
  folderId,
  fileName,
}: {
  folderId: number;
  fileName: string;
}): Promise<FileUploadResponse> => {
  return axios
    .post(
      `${nuclearn_api_url}/project-folders/${folderId}/files/${encodeURIComponent(fileName)}/restore`,
    )
    .then((res) => res.data);
};

export const useRestoreFile = () => {
  const queryClient = useQueryClient();
  return useMutation(restoreFile, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_FILE_RESTORED, {
        folder_id: variables.folderId,
        filename: variables.fileName,
      });
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "project-folder-archived-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "folder-processing-status",
        variables.folderId,
      ]);
    },
  });
};

// === GET ARCHIVED FILES ===
const getArchivedFiles = (id: number): Promise<ProjectFile[]> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${id}/archived-files`, {
      params: { limit: LIST_HARD_CAP },
    })
    .then((res) => res.data.files);
};

export const useArchivedFiles = (id: number | null, enabled = true) => {
  return useQuery(
    ["project-folder-archived-files", id],
    () => getArchivedFiles(id!),
    {
      enabled: enabled && id !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === DELETE ARCHIVED FILE (permanent) ===
const deleteArchivedFile = ({
  folderId,
  fileName,
}: {
  folderId: number;
  fileName: string;
}): Promise<void> => {
  return axios.delete(
    `${nuclearn_api_url}/project-folders/${folderId}/archived-files/${encodeURIComponent(fileName)}`,
  );
};

export const useDeleteArchivedFile = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteArchivedFile, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_ARCHIVED_FILE_DELETED, {
        folder_id: variables.folderId,
        filename: variables.fileName,
      });
      invalidateAfterWrite(queryClient, [
        "project-folder-archived-files",
        variables.folderId,
      ]);
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
    },
  });
};

// === ADD FILE FROM CHAT WORKSPACE / ADHOC UPLOAD ===
const addFromWorkspace = ({
  folderId,
  data,
}: {
  folderId: number;
  data: AddFromWorkspaceRequest;
}): Promise<FileUploadResponse> => {
  return axios
    .post(
      `${nuclearn_api_url}/project-folders/${folderId}/files/from-workspace`,
      data,
    )
    .then((res) => res.data);
};

export const useAddFromWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation(addFromWorkspace, {
    onSuccess: (_data, variables) => {
      invalidateAfterWrite(queryClient, [
        "project-folder-files",
        variables.folderId,
      ]);
    },
  });
};

// === CONTEXT SNAPSHOT (chat header context chip) ===
// The agent-visible file set for the calling user: the whole readable
// forest, matching the FUSE mount and the context injector (spec §5.10).
// User-scoped, not per-dialogue — the anchor folder only labels the chat.
const getContextSnapshot = (): Promise<ContextSnapshotResponse> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/context-snapshot`)
    .then((res) => res.data);
};

export const useContextSnapshot = (enabled = true) => {
  return useQuery(["project-context-snapshot"], getContextSnapshot, {
    enabled,
    refetchOnWindowFocus: false,
    // Shorter than the folder-list staleTime: the chip claims to show the
    // per-turn snapshot, so a popover re-open after an upload should
    // refresh quickly.
    staleTime: 15_000,
  });
};

// === FOLDER-SCOPED CHAT LIST ===
export interface FolderChatSummary {
  id: number;
  name: string | null;
  create_date: string;
  is_visible: boolean;
}

interface FolderChatPage {
  items: FolderChatSummary[];
  total: number;
}

// Backend bounds `limit` at 100; page smaller so a long history doesn't ship
// one giant payload and the dropdown can lazily load more on scroll.
const FOLDER_CHATS_PAGE_SIZE = 25;

const getFolderChats = ({
  folderId,
  skip,
  limit,
  searchQuery,
}: {
  folderId: number;
  skip: number;
  limit: number;
  searchQuery?: string;
}): Promise<FolderChatPage> => {
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue`, {
      params: {
        folder_id: folderId,
        skip,
        limit,
        is_visible: true,
        ...(searchQuery ? { search_query: searchQuery } : {}),
      },
    })
    .then((res) => ({
      items: res.data?.items ?? [],
      total: res.data?.total ?? 0,
    }));
};

/**
 * Paginated, searchable folder chat history. Search filters by chat name
 * server-side; the empty exclude_empty_messages filter keeps turnless chats
 * out. Consumers read `data.pages.flatMap(p => p.items)`.
 */
export const useFolderChats = (
  folderId: number | null,
  searchQuery = "",
  /** Hosts that only read the list once a menu opens gate the fetch on that. */
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const trimmed = searchQuery.trim();
  return useInfiniteQuery(
    ["folder-chats", folderId, trimmed],
    ({ pageParam = 0 }) =>
      getFolderChats({
        folderId: folderId!,
        skip: pageParam,
        limit: FOLDER_CHATS_PAGE_SIZE,
        searchQuery: trimmed || undefined,
      }),
    {
      enabled: enabled && folderId !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      getNextPageParam: (_lastPage, pages) => {
        const loaded = pages.reduce((n, p) => n + p.items.length, 0);
        const total = pages[0]?.total ?? 0;
        return loaded < total ? loaded : undefined;
      },
    },
  );
};

// === ATTACH FOLDER FILE TO DIALOGUE ===
export interface AttachFolderFileResponse {
  dataset_id: number;
  dataset_name: string;
  source_data_record_id: number;
  artifact_id: number;
  filename: string;
}

const attachFolderFileToDialogue = ({
  dialogueId,
  folderId,
  filename,
}: {
  dialogueId: number;
  folderId: number;
  filename: string;
}): Promise<AttachFolderFileResponse> => {
  return axios
    .post(
      `${nuclearn_api_url}/chat/dialogue/${dialogueId}/attach-folder-file`,
      { folder_id: folderId, filename },
    )
    .then((res) => res.data);
};

export const useAttachFolderFileToDialogue = () => {
  return useMutation(attachFolderFileToDialogue, {
    onSuccess: (_data, variables) => {
      captureEvent(AA_EVENTS.FOLDER_FILE_ATTACHED_TO_CHAT, {
        folder_id: variables.folderId,
        dialogue_id: variables.dialogueId,
        filename: variables.filename,
      });
    },
  });
};

// === FOLDER CONTENT SEARCH ===
const getFolderSearch = (
  folderId: number,
  q: string,
  topN = 50,
): Promise<FolderSearchResponse> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/${folderId}/search`, {
      params: { q, top_n: topN },
    })
    .then((res) => res.data);
};

export const useFolderSearch = (
  folderId: number | null | undefined,
  query: string,
  topN = 50,
) => {
  const trimmed = query.trim();
  return useQuery(
    ["project-folder-search", folderId, trimmed, topN],
    () => getFolderSearch(folderId!, trimmed, topN),
    {
      enabled: !!folderId && trimmed.length > 0,
      // keepPreviousData was here, but it leaks stale hits across query
      // changes — when the user clears or edits the input, the prior key's
      // results render until the next fetch resolves. The 300ms debounce
      // upstream already suppresses flicker between keystrokes.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );
};

const searchAllFolders = (
  q: string,
  topN = 50,
): Promise<FolderSearchResponse> => {
  return axios
    .get(`${nuclearn_api_url}/project-folders/search`, {
      params: { q, top_n: topN },
    })
    .then((res) => res.data);
};

// Forest-wide folder-name search (folders only), backing the tree-panel
// filter so it matches accessible subfolders, not just top-level projects.
export const useSearchAllFolders = (
  query: string,
  topN = 50,
  // Lets a caller gate the query on something beyond a non-empty term — the
  // Library pane's Projects tab only searches while it is the selected tab.
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const trimmed = query.trim();
  return useQuery(
    ["project-folder-search-all", trimmed, topN],
    () => searchAllFolders(trimmed, topN),
    {
      enabled: enabled && trimmed.length > 0,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );
};
